import { zodResolver } from '@hookform/resolvers/zod';
import { useSuspenseQuery } from '@tanstack/react-query';
import { assert } from 'assert-ts';
import { dequal as deepEqual } from 'dequal/lite';
import { useMemo, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import type { z } from 'zod';
import { handleServerFormError, trackError, ValidationError } from '#api/errors.ts';
import { useSuspenseFeatureFlagsQuery } from '#api/org.queries.ts';
import { routesCountQueryOptions } from '#api/routes.queries.ts';
import { useAddTag, useEditTag } from '#api/tags.queries.ts';
import { AddButton } from '#components/atoms/buttons/add-button/AddButton.tsx';
import { Button } from '#components/atoms/buttons/Button.tsx';
import { ErrorMessage, Input, Label } from '#components/atoms/forms/index.ts';
import { ConfirmChangesBanner } from '#components/patterns/confirmations/confirm-changes-banner/ConfirmChangesBanner.tsx';
import { CancelLinkButton } from '#components/patterns/forms/cancel-link-button/CancelLinkButton.tsx';
import { Field } from '#components/patterns/forms/field/Field.tsx';
import { FieldSet } from '#components/patterns/forms/fieldset/FieldSet.tsx';
import { Spacer } from '#components/patterns/spacer/Spacer.tsx';
import type { TagName } from '#dn-types/host.ts';
import { useUnknownConfigOverrides } from '#hooks/useUnknownConfigOverrides.ts';
import { RESTART_REQUIRED_SETTINGS_DEFAULT } from '#screens/hosts/form/advanced/form-data.ts';
import { AddFirewallRuleModal } from '#screens/roles/detail/add-firewall-rule-modal/AddFirewallRuleModal.tsx';
import { EditFirewallRuleModal } from '#screens/roles/detail/edit-firewall-rule-modal/EditFirewallRuleModal.tsx';
import { FirewallRuleList } from '#screens/roles/detail/firewall-rule-list/FirewallRuleList.tsx';
import { sortFirewallRules } from '#screens/roles/detail/firewall-rule-list/sortFirewallRules.ts';
import { mapFormValuesToFirewallRules } from '#screens/roles/detail/form/mapFirewallRulesToRoleForm.ts';
import type { FirewallRuleFieldValue } from '#screens/roles/detail/types.ts';
import { mapAPIListErrorsToFormListErrors } from '#util/map-api-list-errors-to-form-list-errors.ts';
import { makeIsEditableConfigOverrideKey, withUnknownOverrides } from '#util/unknownConfigOverrides.ts';
import { checkIsFormValid } from '#util/validation/checkIsFormValid.ts';
import { AddConfigOverrideModal } from './config-overrides/AddConfigOverrideModal.tsx';
import { EditConfigOverrideModal } from './config-overrides/EditConfigOverrideModal.tsx';
import { ConfigOverridesList } from './config-overrides/override-list-form/ConfigOverridesList.tsx';
import { RestartTagModal } from './RestartTagModal.tsx';
import { AddRouteModal } from './routes/AddRouteModal.tsx';
import { RoutesList } from './routes/RoutesList.tsx';
import { tagFormSchema } from './tag-form-schema.ts';
import { mergeAllowListOverrides } from './util/merge-allow-list-overrides.ts';

type EditProps = {
  onSuccess: () => void;
  /** Must be provided when editing an existing tag */
  tagName: TagName;
  hostCount: number;
  defaultValues: z.input<typeof tagFormSchema>;
  duplicateOf?: never;
};

type NewProps = {
  onSuccess: () => void;
  /** Only provided when editing an existing tag */
  tagName: null;
  hostCount: null;
  /** Prefills the form, e.g. when duplicating an existing tag */
  defaultValues?: z.input<typeof tagFormSchema>;
  /** The tag this one is a copy of; the new tag is created adjacent to it in priority order */
  duplicateOf?: TagName;
};

type Props = NewProps | EditProps;

const DEFAULT_VALUES = {
  name: '' as const,
  description: '',
  configOverrides: [],
  routeSubscriptions: [],
  firewallRules: [],
};

export function TagForm(props: Props) {
  const { tagName, onSuccess } = props;
  const { data: routeCount } = useSuspenseQuery(routesCountQueryOptions);
  const { data: { DNS_CONFIG_OVERRIDES } = {} } = useSuspenseFeatureFlagsQuery();
  const savedValues = props.defaultValues ?? DEFAULT_VALUES;
  const [restartRequiredTagName, setRestartRequiredTagName] = useState<TagName | null>(null);

  // Overrides with no editor here would fail the form's discriminated-union parse on save.
  // Keep them out of the form; they render read-only and re-attach on submit unless deleted.
  const {
    knownOverrides,
    unknownOverrides,
    savedUnknownOverrides,
    removedUnknownIndices,
    removeUnknownOverride,
    restoreUnknownOverride,
    hasRemovedUnknownOverrides,
  } = useUnknownConfigOverrides(
    savedValues.configOverrides,
    makeIsEditableConfigOverrideKey(Boolean(DNS_CONFIG_OVERRIDES))
  );
  const defaultValues = useMemo(
    () => ({ ...savedValues, configOverrides: knownOverrides }),
    [savedValues, knownOverrides]
  );

  const formContext = useForm({
    resolver: zodResolver(tagFormSchema),
    defaultValues,
  });

  const isEditing = Boolean(tagName?.length);

  const {
    handleSubmit,
    register,
    control,
    setError,
    clearErrors,
    formState: { errors, isDirty, isSubmitting, isSubmitted },
  } = formContext;
  // Avoid formState.isValid because it changes from false -> true on mount, causes re-render, breaks stories
  const isValid = checkIsFormValid(errors);

  const addTag = useAddTag();
  const editTag = useEditTag();

  const {
    fields: configOverrides,
    remove: removeConfigOverrides,
    append: appendConfigOverrides,
    update: updateConfigOverride,
  } = useFieldArray({
    control,
    name: 'configOverrides',
  });
  const [isAddingConfigOverride, setIsAddingConfigOverride] = useState<boolean>(false);
  const [editConfigOverrideIndex, setEditConfigOverrideIndex] = useState<number | null>(null);

  const {
    fields: routeSubscriptions,
    remove: removeRouteSubscriptions,
    append: appendRouteSubscriptions,
  } = useFieldArray({
    control,
    name: 'routeSubscriptions',
  });
  const [isAddingRoute, setIsAddingRoute] = useState<boolean>(false);

  const {
    fields: firewallRuleFields,
    remove: removeFirewallRule,
    append: appendFirewallRule,
    update: updateFirewallRule,
  } = useFieldArray({
    control,
    name: 'firewallRules',
  });
  // null = closed; defaultValues prefill the add modal when duplicating an existing rule
  const [addFirewallRuleModal, setAddFirewallRuleModal] = useState<{
    defaultValues?: FirewallRuleFieldValue;
  } | null>(null);
  const [editingFirewallRuleIndex, setEditingFirewallRuleIndex] = useState<number | null>(null);
  // We sort the rules before passing them to the table, but must operate on their original indices
  // when editing/removing, due to react-hook-form's field-array APIs.
  const firewallRuleIndices = new Map(firewallRuleFields.map(({ id }, index) => [id, index]));
  const sortedFirewallRules = useMemo(() => sortFirewallRules(firewallRuleFields), [firewallRuleFields]);

  const onSubmit = handleSubmit(async (data) => {
    clearErrors('root.global');

    try {
      const mergedOverrides = withUnknownOverrides(mergeAllowListOverrides(data.configOverrides), unknownOverrides);
      const mappedRouteSubscriptions = data.routeSubscriptions.map(({ routeId }) => routeId);
      const firewallRules = mapFormValuesToFirewallRules(data.firewallRules);
      if (isEditing) {
        const updatedTag = await editTag.mutateAsync({
          name: data.name,
          variables: {
            description: data.description,
            configOverrides: mergedOverrides,
            routeSubscriptions: mappedRouteSubscriptions,
            firewallRules,
          },
        });
        // Has the user changed a setting that requires a restart?  If so, let the user know
        if (
          // Only show a restart required if the tag has dependent hosts
          // NOTE: this does not take into account the version of dnclient on the hosts, and may give false positives
          props.hostCount &&
          props.hostCount > 0 &&
          RESTART_REQUIRED_SETTINGS_DEFAULT.some((key) => {
            return !deepEqual(
              mergedOverrides.find((c) => c.key === key),
              props.defaultValues.configOverrides.find((c) => c.key === key)
            );
          })
        ) {
          // call onSuccess in the modal
          setRestartRequiredTagName(updatedTag.name);
        } else {
          // No restart required for a tag that has no hosts
          onSuccess();
        }
      } else {
        await addTag.mutateAsync({
          name: data.name,
          description: data.description,
          configOverrides: mergedOverrides,
          routeSubscriptions: mappedRouteSubscriptions,
          firewallRules,
          // Place a duplicate directly below its source: "before" in the API's ascending
          // priority order is one row down in the UI's descending list
          ...(props.duplicateOf ? { before: props.duplicateOf } : {}),
        });
        // No restart required for a tag that has no hosts
        onSuccess();
      }
    } catch (error) {
      if (error instanceof ValidationError) {
        handleServerFormError(error, ({ path, message }) => {
          if (path?.startsWith('configOverrides[')) {
            const mappedFieldPath = mapAPIListErrorsToFormListErrors('configOverrides', path);

            setError(mappedFieldPath, { message }, { shouldFocus: true });
            return true;
          }

          if (path?.startsWith('routeSubscriptions[')) {
            const mappedFieldPath = mapAPIListErrorsToFormListErrors('routeSubscriptions', path);

            setError(mappedFieldPath, { message }, { shouldFocus: true });
            return true;
          }

          if (path?.startsWith('firewallRules[')) {
            const mappedFieldPath = mapAPIListErrorsToFormListErrors('firewallRules', path);

            setError(mappedFieldPath, { message }, { shouldFocus: true });
            return true;
          }

          switch (path) {
            case 'name':
            case 'description':
            case 'configOverrides':
            case 'routeSubscriptions':
            case 'firewallRules':
              setError(path, { message }, { shouldFocus: true });
              return true;
            case 'root.global':
            case undefined:
              setError('root.global', { message });
              return true;
            default:
              setError('root.global', { message });
              return false;
          }
        });
      } else {
        trackError(error);
        setError('root.global', { message: `We couldn’t ${isEditing ? 'edit' : 'add'} the tag, please try again` });
      }
    }
  });

  const valueBeingEdited = editConfigOverrideIndex !== null && configOverrides[editConfigOverrideIndex];

  return (
    <FormProvider {...formContext}>
      <form method="post" id="tag-form" onSubmit={onSubmit}>
        <Spacer direction="vertical" size="xxwide" className="mb-24">
          {errors.root?.global?.message && <ErrorMessage>{errors.root.global.message}</ErrorMessage>}
          {isEditing ?
            <Field label="Description">
              <Input
                className="width-100"
                maxLength={256}
                hasError={Boolean(errors.description)}
                {...register('description')}
              />
              {errors.description && <ErrorMessage>{errors.description.message}</ErrorMessage>}
            </Field>
          : <FieldSet
              legend="Define tag"
              helpText="Tags are facets of identity and take the form “key:value” (e.g. “env:prod”)."
            >
              <Field label="Tag">
                <Input
                  className="width-100"
                  maxLength={256}
                  hasError={Boolean(errors.name)}
                  {...register('name')}
                  autoComplete="off"
                />
                {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
              </Field>
              <Field label="Description">
                <Input
                  className="width-100"
                  maxLength={256}
                  hasError={Boolean(errors.description)}
                  {...register('description')}
                />
                {errors.description && <ErrorMessage>{errors.description.message}</ErrorMessage>}
              </Field>
            </FieldSet>
          }
          <FieldSet
            legend="Inbound firewall rules"
            helpText="Inbound traffic is denied by default. Add rules to allow traffic from hosts belonging to specific roles or tags."
          >
            {errors.firewallRules?.root && <ErrorMessage>{errors.firewallRules.root.message}</ErrorMessage>}
            {errors.firewallRules?.message && <ErrorMessage>{errors.firewallRules.message}</ErrorMessage>}
            {Array.isArray(errors.firewallRules) ?
              errors.firewallRules
                .map((error, index) =>
                  error?.message ?
                    // eslint-disable-next-line react/no-array-index-key -- errors are positional and ephemeral
                    <ErrorMessage key={index}>{error.message}</ErrorMessage>
                  : null
                )
                .filter((error) => Boolean(error))
            : null}
            <Field>
              <Label className="visually-hidden">Inbound firewall rules</Label>
              <FirewallRuleList
                firewallRules={sortedFirewallRules}
                hideOverflowX={true}
                // No empty-state banner for tags; the "Add firewall rule" button is enough.
                emptyStateMessage={null}
                onRemove={(id) => {
                  removeFirewallRule(assert(firewallRuleIndices.get(id)));
                }}
                onEdit={(id) => {
                  setEditingFirewallRuleIndex(assert(firewallRuleIndices.get(id)));
                }}
                onDuplicate={(id) => {
                  const rule = assert(
                    firewallRuleFields[assert(firewallRuleIndices.get(id))],
                    'expected duplicated rule to exist'
                  );
                  setAddFirewallRuleModal({ defaultValues: { ...rule, id: '' } });
                }}
              />
              <AddButton
                onClick={() => {
                  setAddFirewallRuleModal({});
                }}
              >
                Add firewall rule
              </AddButton>
            </Field>
          </FieldSet>
          <FieldSet
            legend="Host advanced config"
            helpText="Optionally apply configuration to hosts, lighthouses, and relays with this tag."
          >
            {errors.configOverrides?.root && <ErrorMessage>{errors.configOverrides.root.message}</ErrorMessage>}
            {errors.configOverrides?.message && <ErrorMessage>{errors.configOverrides.message}</ErrorMessage>}
            {Array.isArray(errors.configOverrides) ?
              errors.configOverrides
                .map((error, index) =>
                  error?.message ?
                    // eslint-disable-next-line react/no-array-index-key -- errors are positional and ephemeral
                    <ErrorMessage key={index}>{error.message}</ErrorMessage>
                  : null
                )
                .filter((error) => Boolean(error))
            : null}
            <Field>
              <Label className="visually-hidden">Host advanced config</Label>
              <ConfigOverridesList
                items={configOverrides}
                onEdit={(index) => {
                  setEditConfigOverrideIndex(index);
                }}
                onRemove={removeConfigOverrides}
                unknownItems={savedUnknownOverrides}
                removedUnknownIndices={removedUnknownIndices}
                onRemoveUnknown={removeUnknownOverride}
                onRestoreUnknown={restoreUnknownOverride}
              >
                <AddButton
                  onClick={() => {
                    setIsAddingConfigOverride(true);
                  }}
                >
                  Add config
                </AddButton>
              </ConfigOverridesList>
            </Field>
          </FieldSet>
          {/* Only show routes config if there are any routes to choose from */}
          {routeCount && routeCount > 0 ?
            <FieldSet legend="Routes">
              {errors.routeSubscriptions?.root && <ErrorMessage>{errors.routeSubscriptions.root.message}</ErrorMessage>}
              {errors.routeSubscriptions?.message && <ErrorMessage>{errors.routeSubscriptions.message}</ErrorMessage>}
              {Array.isArray(errors.routeSubscriptions) ?
                errors.routeSubscriptions
                  .map((error, index) =>
                    error?.message ?
                      // eslint-disable-next-line react/no-array-index-key -- errors are positional and ephemeral
                      <ErrorMessage key={index}>{error.message}</ErrorMessage>
                    : null
                  )
                  .filter((error) => Boolean(error))
              : null}
              <Field>
                <Label className="visually-hidden">Routes</Label>
                <RoutesList items={routeSubscriptions} onRemove={removeRouteSubscriptions}>
                  <AddButton
                    onClick={() => {
                      setIsAddingRoute(true);
                    }}
                  >
                    Add route
                  </AddButton>
                </RoutesList>
              </Field>
            </FieldSet>
          : null}
        </Spacer>
        <ConfirmChangesBanner
          isOpen={isDirty || hasRemovedUnknownOverrides}
          title={isSubmitted && !isValid ? 'Unable to save.  Please update and try again.' : undefined}
          primaryButton={
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              Save
            </Button>
          }
          secondaryButton={<CancelLinkButton fallbackTo="/tags" />}
        />
      </form>

      {/* Add/Edit modals for config overrides */}
      {isAddingConfigOverride && (
        <AddConfigOverrideModal
          existingOverrides={configOverrides}
          onSave={(values) => {
            appendConfigOverrides(values);
            setIsAddingConfigOverride(false);
          }}
          onCancel={() => {
            setIsAddingConfigOverride(false);
          }}
        />
      )}
      {valueBeingEdited ?
        <EditConfigOverrideModal
          configType={valueBeingEdited.key === 'stats.type' ? valueBeingEdited.value : valueBeingEdited.key}
          value={valueBeingEdited}
          existingOverrides={configOverrides}
          onSave={(values) => {
            if (valueBeingEdited.key === 'stats.type') {
              // Remove all the 'stats' fields, and re-add the new ones
              const statsIndices = configOverrides
                .map((o, i) => (o.key.startsWith('stats.') ? i : null))
                .filter((i): i is number => i !== null);
              removeConfigOverrides(statsIndices);
              appendConfigOverrides(values);
            } else if (values.length === 1 && values[0]) {
              updateConfigOverride(editConfigOverrideIndex, values[0]);
            }
            setEditConfigOverrideIndex(null);
          }}
          onCancel={() => {
            setEditConfigOverrideIndex(null);
          }}
        />
      : null}
      {/* Add modal for routes */}
      {isAddingRoute && (
        <AddRouteModal
          existingRoutes={routeSubscriptions}
          onSave={(values) => {
            appendRouteSubscriptions(values);
            setIsAddingRoute(false);
          }}
          onCancel={() => {
            setIsAddingRoute(false);
          }}
        />
      )}
      {addFirewallRuleModal && (
        <AddFirewallRuleModal
          defaultValues={addFirewallRuleModal.defaultValues}
          onSave={(values) => {
            appendFirewallRule(values);
            setAddFirewallRuleModal(null);
          }}
          onClose={() => {
            setAddFirewallRuleModal(null);
          }}
        />
      )}
      {editingFirewallRuleIndex != null ?
        <EditFirewallRuleModal
          firewallRule={assert(
            firewallRuleFields[editingFirewallRuleIndex],
            'expected editingFirewallRuleIndex to find firewall rules'
          )}
          onSave={(values) => {
            updateFirewallRule(assert(firewallRuleIndices.get(values.id)), values);
            setEditingFirewallRuleIndex(null);
          }}
          onClose={() => {
            setEditingFirewallRuleIndex(null);
          }}
        />
      : null}
      {restartRequiredTagName ?
        <RestartTagModal
          tagName={restartRequiredTagName}
          onClose={() => {
            onSuccess();
            setRestartRequiredTagName(null);
          }}
        />
      : null}
    </FormProvider>
  );
}
